// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Builds the per-flight row consumed by trend tables, evidence panels, and
// trend chart logic. Pulled out of app.js so the giant flatten-everything
// function can be unit-tested in isolation.
//
// buildTrendRow takes (entry, points, summary, context). The context bag
// holds the helper functions that still live in app.js — most notably the
// closure-bound sensor-summary helpers from createSensorSummaryHelpers() and
// the local quantile / formatPointTime callbacks.

import { cylinderKeys, cylinderNumberFromKey } from "../core/schema.js";
import {
  ENGINE_LABEL_STYLES,
  engineMetricValue,
  engineScopedCylinderLabel,
} from "../core/engines.js";
import { extentFromValues } from "../core/math.js";
import { engineRunningTrendRows, finiteValues, quantile as sampleQuantile } from "../analysis/utils.js";
import { oilSystemParameters } from "../analysis/detectors/oil-system.js";
import { turboTemperatureValues } from "../analysis/turbo.js";
import { combustionEvidenceMeta } from "../analysis/combustion.js";
import { buildTrendOperatingContext } from "./trend-comparison.js";
import {
  correctedFlightTimestamp,
  flightDateFromText,
  flightDisplayTitle,
  plausibleFlightTimestamp,
} from "../app/flight-display-model.js";

export function valuesForKeys(data, keys) {
  const values = [];
  data.forEach((point) => {
    keys.forEach((key) => {
      if (Number.isFinite(point[key])) values.push(point[key]);
    });
  });
  return values;
}

export const chtHeatBands = Object.freeze([
  { key: "325-349", label: "325-349 F", min: 325, max: 350 },
  { key: "350-374", label: "350-374 F", min: 350, max: 375 },
  { key: "375-399", label: "375-399 F", min: 375, max: 400 },
  { key: "400-424", label: "400-424 F", min: 400, max: 425 },
  { key: "425+", label: "425+ F", min: 425, max: Infinity },
]);

function pointDurationSeconds(points, index) {
  const current = points[index]?.elapsedSeconds;
  const next = points[index + 1]?.elapsedSeconds;
  if (Number.isFinite(current) && Number.isFinite(next) && next > current) {
    return next - current <= 60 ? next - current : 0;
  }
  return 0;
}

function averageFinite(values) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return null;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

function medianFinite(values) {
  const finite = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!finite.length) return null;
  const middle = Math.floor(finite.length / 2);
  return finite.length % 2
    ? finite[middle]
    : (finite[middle - 1] + finite[middle]) / 2;
}

function firstFiniteForKeys(point, keys) {
  for (const key of keys) {
    if (Number.isFinite(point?.[key])) return point[key];
  }
  return null;
}

function inferredEngineStartTime(points) {
  const runningCue = points.find((point) => {
    const rpm = firstFiniteForKeys(point, ["rpm", "rpm1", "rpm2"]);
    const fuelFlow = firstFiniteForKeys(point, ["fuelFlow", "fuelFlow1", "fuelFlow2"]);
    return (Number.isFinite(rpm) && rpm >= 500)
      || (Number.isFinite(fuelFlow) && fuelFlow >= 0.8);
  });
  return Number.isFinite(runningCue?.elapsedSeconds) ? runningCue.elapsedSeconds : null;
}

function oilPressureRunningWhiskerPoint(point) {
  const phase = String(point?.phase ?? "");
  if (/\b(?:startup|start|shutdown|shut\s*down)\b/i.test(phase)) return false;
  const rpm = firstFiniteForKeys(point, ["rpm", "rpm1", "rpm2"]);
  if (Number.isFinite(rpm)) return rpm > oilSystemParameters.runningRpm;
  const fuelFlow = firstFiniteForKeys(point, ["fuelFlow", "fuelFlow1", "fuelFlow2"]);
  if (Number.isFinite(fuelFlow)) return fuelFlow >= oilSystemParameters.groundConcernFuelFlowGph;
  return oilSystemParameters.flightPowerPhases.includes(phase);
}

function oilPressurePostStartValues(points, options = {}) {
  const explicitStartTime = options.startTime ?? options.engineStartTime;
  const startTime = Number.isFinite(explicitStartTime) ? explicitStartTime : inferredEngineStartTime(points);
  return valuesForKeys(points.filter((point) => {
    if (!oilPressureRunningWhiskerPoint(point)) return false;
    if (!Number.isFinite(startTime)) return true;
    const elapsed = point?.elapsedSeconds;
    return !Number.isFinite(elapsed) || elapsed >= startTime;
  }), ["oilPressure", "oilPressure1", "oilPressure2"]);
}

function trendWarmChtPoint(point, chtKeys) {
  const avgCht = averageFinite(chtKeys.map((key) => point[key]));
  if (!Number.isFinite(avgCht) || avgCht < 210) return false;
  return !Number.isFinite(point.fuelFlow) || point.fuelFlow >= 5.5;
}

function positiveCylinder(value) {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function cylinderFromMetricTop(top = {}) {
  return positiveCylinder(top.cylinder) ?? cylinderNumberFromKey(top.key) ?? null;
}

function chtCylinderDescriptors(engineDescriptors = [], points = []) {
  return cylinderDescriptorsForPrefix(engineDescriptors, "cht", points);
}

// Per-cylinder descriptors for any channel prefix ("cht", "egt", ...). The engine
// model carries a `${prefix}Key` per cylinder (chtKey / egtKey); when no engine
// model is present we infer the logged cylinder keys from finite samples rather
// than materializing every canonical egt1..N / cht1..N slot.
function presentCylinderKeysForPrefix(points = [], prefix = "cht") {
  const keys = cylinderKeys(prefix);
  const pts = Array.isArray(points) ? points : [];
  return keys.filter((key) => pts.some((point) => Number.isFinite(point?.[key])));
}

function cylinderDescriptorsForPrefix(engineDescriptors = [], prefix = "cht", points = []) {
  const keyField = `${prefix}Key`;
  const descriptors = Array.isArray(engineDescriptors) ? engineDescriptors : [];
  const cylinders = descriptors.flatMap((engine) => (engine?.cylinders ?? [])
    .filter((cylinder) => cylinder?.[keyField])
    .map((cylinder) => {
      const sourceCylinder = positiveCylinder(cylinder.sourceNumber) ?? cylinderNumberFromKey(cylinder[keyField]);
      const displayCylinder = positiveCylinder(cylinder.number) ?? sourceCylinder;
      return {
        cylinder: sourceCylinder,
        sourceCylinder,
        displayCylinder,
        key: cylinder[keyField],
        engineId: engine?.id ?? null,
        engineLabel: engine?.label ?? null,
        engineShortLabel: engine?.shortLabel ?? null,
        label: engineScopedCylinderLabel(sourceCylinder, descriptors, "Cyl", { style: ENGINE_LABEL_STYLES.NARRATIVE }),
        shortLabel: engineScopedCylinderLabel(sourceCylinder, descriptors, "Cyl"),
      };
    }))
    .filter((cylinder) => Number.isFinite(cylinder.sourceCylinder));
  if (cylinders.length) return cylinders;
  return presentCylinderKeysForPrefix(points, prefix).map((key) => {
    const sourceCylinder = cylinderNumberFromKey(key);
    return {
      cylinder: sourceCylinder,
      sourceCylinder,
      displayCylinder: sourceCylinder,
      key,
      engineId: null,
      engineLabel: null,
      engineShortLabel: null,
      label: `Cyl ${sourceCylinder}`,
      shortLabel: `Cyl ${sourceCylinder}`,
    };
  });
}

function chtCylinderSummaryIdentity(summary) {
  return {
    cylinder: summary.cylinder,
    sourceCylinder: summary.sourceCylinder,
    displayCylinder: summary.displayCylinder,
    key: summary.key,
    engineId: summary.engineId,
    engineLabel: summary.engineLabel,
    engineShortLabel: summary.engineShortLabel,
    label: summary.label,
    shortLabel: summary.shortLabel,
  };
}

export function chtHeatBandSummary(points, options = {}) {
  const byCylinder = chtCylinderDescriptors(options.engineDescriptors, points).map((descriptor) => ({
    ...descriptor,
    bands: chtHeatBands.map((band) => ({ ...band, seconds: 0, samples: 0 })),
    totalSeconds: 0,
    operatingSeconds: 0,
  }));
  const keys = byCylinder.map((summary) => summary.key);
  points.forEach((point, pointIndex) => {
    if (!trendWarmChtPoint(point, keys)) return;
    const seconds = pointDurationSeconds(points, pointIndex);
    byCylinder.forEach((summary) => {
      const value = point[summary.key];
      if (!Number.isFinite(value)) return;
      summary.operatingSeconds += seconds;
      const band = summary.bands.find((item) => value >= item.min && value < item.max);
      if (!band) return;
      band.seconds += seconds;
      band.samples += 1;
      summary.totalSeconds += seconds;
    });
  });
  return byCylinder
    .filter((summary) => summary.operatingSeconds > 0)
    .map((summary) => ({
      ...chtCylinderSummaryIdentity(summary),
      bands: summary.bands.map((band) => ({
        key: band.key,
        label: band.label,
        min: band.min,
        max: Number.isFinite(band.max) ? band.max : null,
        seconds: band.seconds,
        minutes: band.seconds / 60,
        samples: band.samples,
      })),
      totalSeconds: summary.totalSeconds,
      totalMinutes: summary.totalSeconds / 60,
      operatingSeconds: summary.operatingSeconds,
      operatingMinutes: summary.operatingSeconds / 60,
    }));
}

export function chtDistributionSummary(points, options = {}) {
  const binMin = 200;
  const binMax = 450;
  const binSize = 5;
  const bins = Array.from({ length: Math.floor((binMax - binMin) / binSize) + 1 }, (_, index) => binMin + index * binSize);
  const byCylinder = chtCylinderDescriptors(options.engineDescriptors, points).map((descriptor) => ({
    ...descriptor,
    bins: bins.map((start) => ({ start, count: 0, seconds: 0 })),
    samples: 0,
    values: [],
  }));
  const keys = byCylinder.map((summary) => summary.key);
  points.forEach((point, pointIndex) => {
    if (!trendWarmChtPoint(point, keys)) return;
    const seconds = pointDurationSeconds(points, pointIndex);
    byCylinder.forEach((summary) => {
      const value = point[summary.key];
      if (!Number.isFinite(value)) return;
      const start = Math.floor(value / binSize) * binSize;
      let bin = summary.bins.find((item) => item.start === start);
      if (!bin) {
        bin = { start, count: 0, seconds: 0 };
        summary.bins.push(bin);
      }
      bin.count += 1;
      bin.seconds += seconds;
      summary.samples += 1;
      summary.values.push(value);
    });
  });
  return byCylinder
    .filter((summary) => summary.samples > 0)
    .map((summary) => ({
      ...chtCylinderSummaryIdentity(summary),
      samples: summary.samples,
      p95: sampleQuantile(summary.values, 0.95),
      bins: summary.bins.sort((a, b) => a.start - b.start),
    }));
}

// Raw per-cylinder CHT values behind the distribution lanes: the same warm
// point gating as chtDistributionSummary, but every finite sample is kept (no
// bin range cap) so report statistics can compute exact quantiles and a true
// peak instead of reconstructing samples from capped histogram bins.
export function chtDistributionValues(points, options = {}) {
  const byCylinder = chtCylinderDescriptors(options.engineDescriptors, points).map((descriptor) => ({
    ...descriptor,
    values: [],
  }));
  const keys = byCylinder.map((summary) => summary.key);
  (Array.isArray(points) ? points : []).forEach((point) => {
    if (!trendWarmChtPoint(point, keys)) return;
    byCylinder.forEach((summary) => {
      const value = point[summary.key];
      if (Number.isFinite(value)) summary.values.push(value);
    });
  });
  return byCylinder
    .filter((summary) => summary.values.length > 0)
    .map((summary) => ({
      ...chtCylinderSummaryIdentity(summary),
      samples: summary.values.length,
      values: summary.values,
    }));
}

// Loaded/cruise proxy for the EGT Overview visuals. EGT balance only reads
// meaningfully under load (the score itself is loaded-context aware); idle/taxi
// EGT spread is naturally wide and expected. We don't import the detector's
// internal classifier — this is a deliberately simple, robust proxy: any one
// power channel high enough, with a cruise-ish EGT floor fallback for EGT-only
// airframes that log no RPM / fuel-flow / MAP.
function egtLoadedSample(point, egtKeys) {
  const flags = [
    Number.isFinite(point?.percentPower) ? point.percentPower >= 45 : null,
    Number.isFinite(point?.rpm) ? point.rpm >= 2000 : null,
    Number.isFinite(point?.rpm1) ? point.rpm1 >= 2000 : null,
    Number.isFinite(point?.rpm2) ? point.rpm2 >= 2000 : null,
    Number.isFinite(point?.fuelFlow) ? point.fuelFlow >= 6 : null,
    Number.isFinite(point?.map) ? point.map >= 19 : null,
  ].filter((flag) => flag !== null);
  if (flags.length) return flags.some(Boolean);
  const avgEgt = averageFinite(egtKeys.map((key) => point[key]));
  return Number.isFinite(avgEgt) && avgEgt >= 1100;
}

function quantileSorted(sorted, fraction) {
  if (!sorted.length) return null;
  const position = Math.max(0, Math.min(sorted.length - 1, (sorted.length - 1) * fraction));
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

// Per-cylinder EGT distribution (where each cylinder's EGT spent its loaded time),
// mirroring chtDistributionSummary but on the EGT scale and gated to loaded
// samples so cylinder-to-cylinder balance reads cleanly. Same per-cylinder
// { samples, bins:[{start,count,seconds}] } shape the distribution-lane grammar
// consumes.
export function egtDistributionSummary(points, options = {}) {
  const binMin = 900;
  const binMax = 1750;
  const binSize = 10;
  const bins = Array.from({ length: Math.floor((binMax - binMin) / binSize) + 1 }, (_, index) => binMin + index * binSize);
  const byCylinder = cylinderDescriptorsForPrefix(options.engineDescriptors, "egt", points).map((descriptor) => ({
    ...descriptor,
    bins: bins.map((start) => ({ start, count: 0, seconds: 0 })),
    samples: 0,
  }));
  const keys = byCylinder.map((summary) => summary.key);
  (Array.isArray(points) ? points : []).forEach((point, pointIndex) => {
    if (!egtLoadedSample(point, keys)) return;
    const seconds = pointDurationSeconds(points, pointIndex);
    byCylinder.forEach((summary) => {
      const value = point[summary.key];
      if (!Number.isFinite(value) || value < binMin || value > binMax) return;
      const binIndex = Math.min(summary.bins.length - 1, Math.max(0, Math.floor((value - binMin) / binSize)));
      summary.bins[binIndex].count += 1;
      summary.bins[binIndex].seconds += seconds;
      summary.samples += 1;
    });
  });
  return byCylinder
    .filter((summary) => summary.samples > 0)
    .map((summary) => ({
      ...chtCylinderSummaryIdentity(summary),
      samples: summary.samples,
      bins: summary.bins,
    }));
}

// Raw per-cylinder loaded EGT values behind egtDistributionSummary (same
// loaded-sample gating, no bin range cap). See chtDistributionValues.
export function egtDistributionValues(points, options = {}) {
  const byCylinder = cylinderDescriptorsForPrefix(options.engineDescriptors, "egt", points).map((descriptor) => ({
    ...descriptor,
    values: [],
  }));
  const keys = byCylinder.map((summary) => summary.key);
  (Array.isArray(points) ? points : []).forEach((point) => {
    if (!egtLoadedSample(point, keys)) return;
    byCylinder.forEach((summary) => {
      const value = point[summary.key];
      if (Number.isFinite(value)) summary.values.push(value);
    });
  });
  return byCylinder
    .filter((summary) => summary.values.length > 0)
    .map((summary) => ({
      ...chtCylinderSummaryIdentity(summary),
      samples: summary.values.length,
      values: summary.values,
    }));
}

// Cross-cylinder EGT spread over the flight, for the Overview spread timeline.
// At each running sample we record the hot-minus-cool spread and the P90-P10
// spread (what the balance score reads), plus a loaded flag so the renderer can
// shade the stretch the score actually weighs. Downsampled to keep it light.
export function egtSpreadTimelineSummary(points, options = {}) {
  const keys = cylinderDescriptorsForPrefix(options.engineDescriptors, "egt", points).map((descriptor) => descriptor.key);
  const pts = Array.isArray(points) ? points : [];
  const raw = [];
  pts.forEach((point, index) => {
    const values = keys
      .map((key) => point[key])
      .filter((value) => Number.isFinite(value) && value >= 600 && value <= 2000);
    if (values.length < 2) return;
    const sorted = values.slice().sort((a, b) => a - b);
    raw.push({
      t: Number.isFinite(point?.elapsedSeconds) ? point.elapsedSeconds : index,
      spread: sorted[sorted.length - 1] - sorted[0],
      loaded: egtLoadedSample(point, keys),
      cylinderCount: values.length,
    });
  });
  if (!raw.length) {
    return { samples: [], maxSpread: 0, loadedSpreadP90: null, loadedSpreadMax: null, cylinderCount: 0, durationSeconds: 0 };
  }
  const target = 600;
  const step = Math.max(1, Math.ceil(raw.length / target));
  const samples = step === 1 ? raw : raw.filter((_, index) => index % step === 0);
  // Scored-quantity proxy: P90 (and worst) of the per-sample hot-minus-cool spread
  // over loaded samples — what the balance score reads. Approximate (the detector
  // adds stability filtering we don't replicate), but directionally faithful.
  const loadedSpreads = raw.filter((sample) => sample.loaded).map((sample) => sample.spread).sort((a, b) => a - b);
  return {
    samples,
    maxSpread: Math.max(...raw.map((sample) => sample.spread)),
    loadedSpreadP90: loadedSpreads.length ? quantileSorted(loadedSpreads, 0.9) : null,
    loadedSpreadMax: loadedSpreads.length ? loadedSpreads[loadedSpreads.length - 1] : null,
    cylinderCount: Math.max(...raw.map((sample) => sample.cylinderCount)),
    durationSeconds: raw[raw.length - 1].t - raw[0].t,
  };
}

export function chtByCylinderSummary(points, options = {}) {
  return chtCylinderDescriptors(options.engineDescriptors, points).map((cylinder) => ({
    cylinder: cylinder.sourceCylinder,
    sourceCylinder: cylinder.sourceCylinder,
    displayCylinder: cylinder.displayCylinder,
    key: cylinder.key,
    engineId: cylinder.engineId,
    engineLabel: cylinder.engineLabel,
    engineShortLabel: cylinder.engineShortLabel,
    label: cylinder.label,
    shortLabel: cylinder.shortLabel,
    peak: extentFromValues(finiteValues(points, cylinder.key))?.max ?? null,
  }));
}

// Channel presence derived directly from the logged samples, so DB-mode rows
// gate trend availability on what the flight actually recorded — not on
// incidental score-summary internals (e.g. a charging "basis"), which produced
// false negatives that hid metrics like Mag Check. Multi-key forms cover twins
// that log per-engine rpm1/rpm2, map1/map2, fuelFlow1/fuelFlow2.
export function pointChannelPresence(points) {
  const pts = Array.isArray(points) ? points : [];
  return {
    hasChtData: valuesForKeys(pts, cylinderKeys("cht")).length > 0,
    hasEgtData: valuesForKeys(pts, cylinderKeys("egt")).length > 0,
    hasBusData: finiteValues(pts, "buss").length > 0,
    hasMapData: valuesForKeys(pts, ["map", "map1", "map2"]).length > 0,
    hasRpmData: valuesForKeys(pts, ["rpm", "rpm1", "rpm2"]).length > 0,
    hasFuelFlowData: valuesForKeys(pts, ["fuelFlow", "fuelFlow1", "fuelFlow2"]).length > 0,
    hasCarbTempData: finiteValues(pts, "carbTemp").length > 0,
    hasOatData: finiteValues(pts, "oat").length > 0,
  };
}

export function oilSystemTrendSummary(points, quantile, options = {}) {
  const pressureValues = valuesForKeys(points, ["oilPressure", "oilPressure1", "oilPressure2"]);
  const runningPressureValues = oilPressurePostStartValues(points, options);
  const temperatureValues = valuesForKeys(points, ["oilTemp", "oilTemp1", "oilTemp2"]);
  const envelopeSource = points
    .map((point) => {
      const temperature = firstFiniteForKeys(point, ["oilTemp", "oilTemp1", "oilTemp2"]);
      const pressure = firstFiniteForKeys(point, ["oilPressure", "oilPressure1", "oilPressure2"]);
      const rpm = firstFiniteForKeys(point, ["rpm", "rpm1", "rpm2"]);
      return { temperature, pressure, rpm };
    })
    .filter((point) => (
      Number.isFinite(point.temperature)
      && Number.isFinite(point.pressure)
      && point.temperature >= 120
      && point.temperature <= 240
      && point.pressure >= 20
      && point.pressure <= 110
      && (!Number.isFinite(point.rpm) || point.rpm >= 1000)
    ));
  const sampleStep = Math.max(1, Math.ceil(envelopeSource.length / 1800));
  return {
    hasPressureData: pressureValues.length > 0,
    hasTemperatureData: temperatureValues.length > 0,
    pressureMedian: medianFinite(runningPressureValues),
    pressureP10: quantile(runningPressureValues, 0.1),
    pressureP90: quantile(runningPressureValues, 0.9),
    pressureMin: extentFromValues(runningPressureValues)?.min ?? null,
    pressureMax: extentFromValues(runningPressureValues)?.max ?? null,
    pressurePostStartMin: extentFromValues(runningPressureValues)?.min ?? null,
    pressurePostStartMax: extentFromValues(runningPressureValues)?.max ?? null,
    temperatureMedian: medianFinite(temperatureValues),
    temperatureP10: quantile(temperatureValues, 0.1),
    temperatureP90: quantile(temperatureValues, 0.9),
    temperatureMin: extentFromValues(temperatureValues)?.min ?? null,
    temperatureMax: extentFromValues(temperatureValues)?.max ?? null,
    sampleCount: Math.min(pressureValues.length, temperatureValues.length),
    envelopeSamples: envelopeSource
      .filter((point, index) => index % sampleStep === 0)
      .map((point) => ({
        temperature: Math.round(point.temperature * 10) / 10,
        pressure: Math.round(point.pressure * 10) / 10,
      })),
  };
}

function oilTrendPointsForEngine(points, engine) {
  return (points ?? []).map((point) => ({
    elapsedSeconds: point.elapsedSeconds,
    phase: point.phase,
    oilPressure: engineMetricValue(point, engine, "oilPressure"),
    oilTemp: engineMetricValue(point, engine, "oilTemp"),
    rpm: engineMetricValue(point, engine, "rpm") ?? point?.rpm,
    fuelFlow: engineMetricValue(point, engine, "fuelFlow") ?? point?.fuelFlow,
  }));
}

export function oilTrendSummaryByEngine(points, quantile, engineDescriptors = [], options = {}) {
  const descriptors = Array.isArray(engineDescriptors) ? engineDescriptors : [];
  if (descriptors.length < 2) return [];
  return descriptors
    .map((engine) => {
      const trend = oilSystemTrendSummary(oilTrendPointsForEngine(points, engine), quantile, options);
      return {
        engineId: engine?.id ?? null,
        engineKey: engine?.engineKey ?? null,
        engineLabel: engine?.label ?? null,
        engineShortLabel: engine?.shortLabel ?? engine?.label ?? null,
        ...trend,
      };
    })
    .filter((trend) => trend.hasPressureData || trend.hasTemperatureData || Number.isFinite(trend.pressureMedian) || Number.isFinite(trend.temperatureMedian));
}

export function egtEngineSpreadSummary(egtDetails = {}, engineDescriptors = []) {
  const entries = Array.isArray(egtDetails?.engines) ? egtDetails.engines : [];
  const descriptors = Array.isArray(engineDescriptors) ? engineDescriptors : [];
  return entries
    .map((entry) => {
      const entryKey = String(entry?.engineKey ?? entry?.engineId ?? "").trim();
      const descriptor = descriptors.find((engine) => (
        String(engine?.id ?? "") === entryKey
        || String(engine?.engineKey ?? "") === entryKey
        || String(engine?.engineNumber ?? "") === entryKey
        || String(engine?.label ?? "") === String(entry?.label ?? "")
      ));
      const details = entry?.details ?? {};
      const sampleContext = details.spreadSampleContext ?? {};
      return {
        engineId: descriptor?.id ?? entry?.engineId ?? (entryKey || null),
        engineKey: entry?.engineKey ?? descriptor?.engineKey ?? null,
        engineLabel: descriptor?.label ?? entry?.label ?? null,
        engineShortLabel: descriptor?.shortLabel ?? entry?.shortLabel ?? entry?.label ?? null,
        score: Number.isFinite(entry?.score) ? entry.score : null,
        note: entry?.note ?? null,
        spreadP90: Number.isFinite(details.p90) ? details.p90 : null,
        spreadMax: Number.isFinite(details.max) ? details.max : null,
        spreadAllP90: Number.isFinite(sampleContext.allP90) ? sampleContext.allP90 : null,
        spreadSampleContext: sampleContext.mode ?? null,
        spreadScoringSampleCount: Number.isFinite(sampleContext.scoringSampleCount) ? sampleContext.scoringSampleCount : null,
        spreadLoadedSampleCount: Number.isFinite(sampleContext.loadedSampleCount) ? sampleContext.loadedSampleCount : null,
      };
    })
    .filter((entry) => Number.isFinite(entry.spreadP90) || Number.isFinite(entry.spreadMax) || Number.isFinite(entry.score));
}

// Divergence events shown in evidence panels are restricted to those near the
// top finding (suspicion >= 22% of the top, floored at 10). Exported so the DB
// snapshot builder applies the identical filter instead of dumping every event.
export function filterDivergenceEvents(events, topSuspicion) {
  return (Array.isArray(events) ? events : [])
    .filter((event) => Number.isFinite(event.startTime) && event.suspicion >= Math.max(10, (topSuspicion ?? 0) * 0.22));
}

// The trend-row event arrays each have a purpose-specific shape the evidence
// panels read. Exported so the DB snapshot builder produces byte-identical rows
// instead of a generic superset shape.
export function divergenceEventRows(events, topSuspicion) {
  return filterDivergenceEvents(events, topSuspicion)
    .sort((a, b) => a.startTime - b.startTime)
    .slice(0, 6)
    .map((event) => ({
      startTime: event.startTime ?? null,
      endTime: event.endTime ?? null,
      duration: event.duration ?? null,
      mode: event.mode ?? null,
      egtLift: event.egtLift ?? null,
      chtLift: event.chtLift ?? null,
      cylinders: event.cylinders ?? [],
      powerDetail: event.power?.detail ?? null,
      powerStable: event.power?.stable ?? null,
    }));
}

export function descentEgtDropoutEventRows(events) {
  return (Array.isArray(events) ? events : [])
    .filter((event) => Number.isFinite(event.startTime))
    .sort((a, b) => a.startTime - b.startTime)
    .slice(0, 6)
    .map((event) => ({
      startTime: event.startTime ?? null,
      endTime: event.endTime ?? null,
      cylinder: event.cylinder ?? null,
      dropoutF: event.dropoutF ?? null,
    }));
}

export function coolingRateEventRows(events) {
  return (Array.isArray(events) ? events : [])
    .filter((event) => Number.isFinite(event.startTime) && Number.isFinite(event.endTime))
    .sort((a, b) => a.startTime - b.startTime)
    .map((event) => ({
      startTime: event.startTime ?? null,
      endTime: event.endTime ?? null,
      reviewStartTime: event.reviewStartTime ?? null,
      reviewEndTime: event.reviewEndTime ?? null,
      key: event.key ?? null,
      label: event.label ?? null,
      cylinder: event.cylinder ?? null,
      rateFpm: event.rateFpm ?? null,
      durationSeconds: event.durationSeconds ?? null,
      percentPowerDelta: event.percentPowerDelta ?? null,
      percentPowerBefore: event.percentPowerBefore ?? null,
      percentPowerAfter: event.percentPowerAfter ?? null,
      contextTags: event.contextTags ?? [],
    }));
}

export function hasCarbTempChannel(summary) {
  const metric = summary?.carbIce;
  if (!metric) return false;
  if (Number.isFinite(metric.details?.carbSamples) || Number.isFinite(metric.details?.oatSamples)) {
    return (metric.details?.carbSamples ?? 0) > 0 && (metric.details?.oatSamples ?? 0) > 0;
  }
  const top = metric.details?.top;
  if (Number.isFinite(top?.carbTempMedian) || Number.isFinite(top?.oatMedian)) {
    return Number.isFinite(top?.carbTempMedian) && Number.isFinite(top?.oatMedian);
  }
  if (Number.isFinite(metric.score)) return false;
  return !/no carb(?:uretor)? temperature channel|no carb-temp channel|no oat channel/i.test(metric.note ?? "");
}

export function trendDateValue(entry, points) {
  const source = `${entry.title ?? ""} ${entry.id ?? ""} ${entry.path ?? ""}`;
  const timestamp = correctedFlightTimestamp(points.find((point) => point.timestamp)?.timestamp, source);
  if (timestamp && plausibleFlightTimestamp(timestamp)) {
    const parsed = new Date(timestamp).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  // Prefer the canonical flight_date for the calendar day. flight_start_at is
  // stored UTC by some parsers (Garmin G1000) and local by others, with no
  // timezone designator on the wire, so new Date() reads it as browser-local —
  // landing an evening UTC start on the wrong day, varying by viewer timezone.
  // flight_date is the unambiguous local day; anchor it at noon UTC so it reads
  // as the same day whether formatted in UTC (trendDateLabel) or local time (the
  // panel's date filters / range card). Same-day ordering is restored by the
  // sort's flight_start_at tiebreak. (Slice first: flight_date is persisted as a
  // full timestamp, e.g. "2025-12-22 07:00:00", not a bare date — appending
  // "T12:00:00Z" to the whole thing produced an Invalid Date, so this branch was
  // dead and the code fell through to the naive flight_start_at parse below.)
  const storedDay = String(entry.flight_date ?? "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(storedDay)) {
    const dayMs = new Date(`${storedDay}T12:00:00Z`).getTime();
    if (Number.isFinite(dayMs) && dayMs > 0 && plausibleFlightTimestamp(new Date(dayMs).toISOString())) return dayMs;
  }
  const correctedStart = correctedFlightTimestamp(entry.flight_start_at, source);
  const storedTimestamp = correctedStart ? new Date(correctedStart).getTime() : 0;
  if (
    Number.isFinite(storedTimestamp)
    && storedTimestamp > 0
    && plausibleFlightTimestamp(new Date(storedTimestamp).toISOString())
  ) return storedTimestamp;
  const sourceDate = flightDateFromText(source);
  if (sourceDate) {
    const [year, month, day] = sourceDate.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  }
  const iso = source.match(/(20\d{2})[-_](\d{2})[-_](\d{2})/);
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const us = source.match(/\b(\d{1,2})[-_](\d{1,2})[-_](\d{2,4})\b/);
  if (us) {
    const year = Number(us[3].length === 2 ? `20${us[3]}` : us[3]);
    return Date.UTC(year, Number(us[1]) - 1, Number(us[2]));
  }
  return 0;
}

export function trendDateLabel(value) {
  if (!Number.isFinite(value) || value <= 0) return "Unknown";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "2-digit", timeZone: "UTC" }).format(new Date(value));
}

export function buildTrendRow(entry, points, summary, context = {}) {
  const {
    quantile,
    formatPointTime,
    sensorReviewSummaries,
    sensorSummaryDisplayLabel,
    sensorSummaryEvidenceParts,
    sensorSummaryEvidenceText,
    sensorSummaryPeerMismatchCount,
    instrumentationSuspicion,
  } = context;
  const chtValues = valuesForKeys(points, cylinderKeys("cht"));
  const egtValues = valuesForKeys(points, cylinderKeys("egt"));
  const busValues = finiteValues(points, "buss");
  const channelPresence = pointChannelPresence(points);
  const oilPressureValues = valuesForKeys(points, ["oilPressure", "oilPressure1", "oilPressure2"]);
  const oilTemperatureValues = valuesForKeys(points, ["oilTemp", "oilTemp1", "oilTemp2"]);
  const turboTrendPoints = engineRunningTrendRows(points);
  const turboTemps = turboTemperatureValues(turboTrendPoints);
  const turboMapValues = valuesForKeys(turboTrendPoints, ["map", "map1", "map2"]);
  const upperDeckValues = finiteValues(turboTrendPoints, "upperDeckPressure");
  const busExtent = extentFromValues(busValues);
  const sensorTop = summary.sensors.details.top ?? {};
  const sensorIssues = sensorReviewSummaries(summary.sensors)
    .filter((item) => sensorSummaryEvidenceParts(item).length > 0)
    .slice(0, 8)
    .map((item) => ({
      key: item.key ?? null,
      label: sensorSummaryDisplayLabel(item),
      suspicion: item.suspicion ?? null,
      zeroCount: item.zeroCount ?? 0,
      outOfRangeCount: item.outOfRangeCount ?? 0,
      impossibleJumpCount: item.impossibleJumpCount ?? 0,
      peerMismatchCount: sensorSummaryPeerMismatchCount(item),
      evidence: sensorSummaryEvidenceText(item, 3),
    }));
  const coolingTop = summary.cooling.details.top ?? {};
  const coolingRateTop = summary.coolingRate.details.top ?? {};
  const coolingRateThresholdFpm = summary.coolingRate.details.thresholdFpm ?? null;
  const descentEgtDropoutEvents = descentEgtDropoutEventRows(summary.descentEgtDropout.details.events);
  const coolingRateEvents = coolingRateEventRows(summary.coolingRate.details.events);
  const carbIceTop = summary.carbIce.details.top ?? {};
  const inductionLeakTop = summary.inductionLeak.details.top ?? {};
  const divergenceTop = summary.divergence.details.top ?? {};
  const divergenceEvents = divergenceEventRows(summary.divergence.details.events, divergenceTop.suspicion);
  const turboDetails = summary.turbo.details ?? {};
  const turboLimits = turboDetails.limits ?? {};
  const turboMapLimit = turboLimits.maxContinuousMapInHg
    ?? turboLimits.maxMapInHg
    ?? turboLimits.maxTakeoffMapInHg
    ?? null;
  const turboTitLimit = turboLimits.maxTitF ?? null;
  const turboPeakTit = turboDetails.peakTit ?? extentFromValues(turboTemps)?.max ?? null;
  const turboP95Tit = turboDetails.p95Tit ?? quantile(turboTemps, 0.95);
  const turboPeakMap = turboDetails.peakMap ?? extentFromValues(turboMapValues)?.max ?? null;
  const turboP95Map = turboDetails.p95Map ?? quantile(turboMapValues, 0.95);
  const turboPeakUpperDeck = turboDetails.peakUpperDeck ?? extentFromValues(upperDeckValues)?.max ?? null;
  const combustionTop = summary.combustion.details.top ?? {};
  const turboLimitEvents = (turboDetails.eventWindows ?? []).map((window) => ({
    label: window.label ?? null,
    key: window.key ?? null,
    kind: window.kind ?? null,
    limit: window.limit ?? null,
    peakValue: window.peakValue ?? null,
    startTime: window.startTime ?? null,
    endTime: window.endTime ?? null,
    duration: window.duration ?? null,
  }));
  const first = points[0];
  const last = points.at(-1);
  const durationSeconds = Number.isFinite(last?.elapsedSeconds) ? last.elapsedSeconds : points.length;
  const dateValue = trendDateValue(entry, points);
  const engineDescriptors = Array.isArray(context.engineDescriptors)
    ? context.engineDescriptors
    : Array.isArray(summary.engineDescriptors) ? summary.engineDescriptors : [];
  const chtCylinders = chtCylinderDescriptors(engineDescriptors, points);

  return {
    id: entry.id,
    title: flightDisplayTitle(entry),
    aircraft: entry.aircraft,
    monitor: entry.monitor,
    operatingContext: buildTrendOperatingContext(points, engineDescriptors, entry),
    engineCount: engineDescriptors.length || null,
    cylinderIdentities: chtCylinders.map((cylinder) => ({
      cylinder: cylinder.sourceCylinder,
      sourceCylinder: cylinder.sourceCylinder,
      displayCylinder: cylinder.displayCylinder,
      key: cylinder.key,
      engineId: cylinder.engineId,
      engineLabel: cylinder.engineLabel,
      engineShortLabel: cylinder.engineShortLabel,
      label: cylinder.label,
      shortLabel: cylinder.shortLabel,
    })),
    dateValue,
    dateLabel: trendDateLabel(dateValue),
    durationMinutes: durationSeconds / 60,
    sampleCount: points.length,
    hasChtData: channelPresence.hasChtData,
    hasEgtData: channelPresence.hasEgtData,
    hasBusData: channelPresence.hasBusData,
    hasMapData: channelPresence.hasMapData || Number.isFinite(turboDetails.peakMapLeft) || Number.isFinite(turboDetails.peakMapRight),
    hasRpmData: channelPresence.hasRpmData,
    hasFuelFlowData: channelPresence.hasFuelFlowData,
    hasCarbTempData: channelPresence.hasCarbTempData,
    hasOatData: channelPresence.hasOatData,
    hasOilPressureData: oilPressureValues.length > 0,
    hasOilTemperatureData: oilTemperatureValues.length > 0,
    hasOilData: oilPressureValues.length > 0 || oilTemperatureValues.length > 0,
    hasTurboData: turboTemps.length > 0
      || upperDeckValues.length > 0
      || Number.isFinite(turboDetails.peakMap)
      || Number.isFinite(turboDetails.peakMapLeft)
      || Number.isFinite(turboDetails.peakMapRight),
    overallScore: summary.overall.score,
    oilSystemScore: summary.oilSystem.score,
    chtScore: summary.cht.score,
    egtScore: summary.egt.score,
    valveScore: summary.valve.score,
    combustionScore: summary.combustion.score,
    divergenceScore: summary.divergence.score,
    descentEgtDropoutScore: summary.descentEgtDropout.score,
    inductionLeakScore: summary.inductionLeak.score,
    magScore: summary.mag.score,
    carbIceScore: summary.carbIce.score,
    carbIceAvailable: hasCarbTempChannel(summary),
    turboScore: summary.turbo.score,
    turboAvailable: turboDetails.available === true,
    turboStatus: turboDetails.status ?? null,
    turboNote: summary.turbo.note ?? null,
    turboLimitCount: turboDetails.limitCount ?? 0,
    turboLimitEvents,
    turboTitQualityLabel: turboDetails.titQuality?.label ?? null,
    turboTitQualityScore: turboDetails.titQuality?.score ?? null,
    turboTitQualityPenalty: turboDetails.titQualityPenalty ?? turboDetails.titQuality?.penalty ?? 0,
    turboTitQualityJumps: turboDetails.titQuality?.impossibleJumpCount ?? 0,
    turboTitQualityUnstableSamples: turboDetails.titQuality?.unstableSampleCount ?? 0,
    turboTitQualityMaxJumpDelta: turboDetails.titQuality?.maxJumpDelta ?? null,
    turboMapLimit,
    turboMapMargin: Number.isFinite(turboMapLimit) && Number.isFinite(turboPeakMap) ? turboMapLimit - turboPeakMap : null,
    turboMapP95Margin: Number.isFinite(turboMapLimit) && Number.isFinite(turboP95Map) ? turboMapLimit - turboP95Map : null,
    turboPeakMapLeft: turboDetails.peakMapLeft ?? null,
    turboPeakMapRight: turboDetails.peakMapRight ?? null,
    turboP95MapLeft: turboDetails.p95MapLeft ?? null,
    turboP95MapRight: turboDetails.p95MapRight ?? null,
    turboMapMarginLeft: Number.isFinite(turboMapLimit) && Number.isFinite(turboDetails.peakMapLeft) ? turboMapLimit - turboDetails.peakMapLeft : null,
    turboMapMarginRight: Number.isFinite(turboMapLimit) && Number.isFinite(turboDetails.peakMapRight) ? turboMapLimit - turboDetails.peakMapRight : null,
    turboMapP95MarginLeft: Number.isFinite(turboMapLimit) && Number.isFinite(turboDetails.p95MapLeft) ? turboMapLimit - turboDetails.p95MapLeft : null,
    turboMapP95MarginRight: Number.isFinite(turboMapLimit) && Number.isFinite(turboDetails.p95MapRight) ? turboMapLimit - turboDetails.p95MapRight : null,
    turboTitLimit,
    turboTitMargin: Number.isFinite(turboTitLimit) && Number.isFinite(turboP95Tit) ? turboTitLimit - turboP95Tit : null,
    turboPeakTitMargin: Number.isFinite(turboTitLimit) && Number.isFinite(turboPeakTit) ? turboTitLimit - turboPeakTit : null,
    turboPeakTitLeft: turboDetails.peakTitLeft ?? null,
    turboPeakTitRight: turboDetails.peakTitRight ?? null,
    turboP95TitLeft: turboDetails.p95TitLeft ?? null,
    turboP95TitRight: turboDetails.p95TitRight ?? null,
    turboTitMarginLeft: Number.isFinite(turboTitLimit) && Number.isFinite(turboDetails.p95TitLeft) ? turboTitLimit - turboDetails.p95TitLeft : null,
    turboTitMarginRight: Number.isFinite(turboTitLimit) && Number.isFinite(turboDetails.p95TitRight) ? turboTitLimit - turboDetails.p95TitRight : null,
    turboPeakTitMarginLeft: Number.isFinite(turboTitLimit) && Number.isFinite(turboDetails.peakTitLeft) ? turboTitLimit - turboDetails.peakTitLeft : null,
    turboPeakTitMarginRight: Number.isFinite(turboTitLimit) && Number.isFinite(turboDetails.peakTitRight) ? turboTitLimit - turboDetails.peakTitRight : null,
    turboRecommendedTitMargin: turboLimits.recommendedTitMarginF ?? null,
    turboMapOvershoot: turboDetails.mapOvershoot ?? null,
    turboTitOvershoot: turboDetails.titOvershoot ?? null,
    turboNearTitLimit: turboDetails.nearTitLimit === true,
    coolingScore: summary.cooling.score,
    coolingRateScore: summary.coolingRate.score,
    coolingRateThresholdFpm,
    chargingScore: summary.charging.score,
    electricalScore: summary.electrical.score,
    electricalRunningMin: summary.electrical.details?.runningMin ?? null,
    electricalRunningMax: summary.electrical.details?.runningMax ?? null,
    electricalRunningMedian: summary.electrical.details?.runningMedian ?? null,
    electricalLowLimit: summary.electrical.details?.lowLimit ?? null,
    electricalHighLimit: summary.electrical.details?.highLimit ?? null,
    electricalOutRatio: summary.electrical.details?.outRatio ?? null,
    chtHeatBandsByCylinder: chtHeatBandSummary(points, { engineDescriptors }),
    chtDistributionByCylinder: chtDistributionSummary(points, { engineDescriptors }),
    oilTrend: oilSystemTrendSummary(points, quantile, {
      startTime: summary.startupBehavior?.details?.startTime,
    }),
    oilTrendByEngine: oilTrendSummaryByEngine(points, quantile, engineDescriptors, {
      startTime: summary.startupBehavior?.details?.startTime,
    }),
    peakCht: extentFromValues(chtValues)?.max ?? null,
    p95Cht: quantile(chtValues, 0.95),
    maxEgt: extentFromValues(egtValues)?.max ?? null,
    egtSpreadP90: summary.egt.details.p90 ?? null,
    egtSpreadSampleContext: summary.egt.details.spreadSampleContext?.mode ?? null,
    egtSpreadScoringSampleCount: summary.egt.details.spreadSampleContext?.scoringSampleCount ?? null,
    egtSpreadLoadedSampleCount: summary.egt.details.spreadSampleContext?.loadedSampleCount ?? null,
    egtSpreadAllP90: summary.egt.details.spreadSampleContext?.allP90 ?? null,
    egtEngineSpreads: egtEngineSpreadSummary(summary.egt.details, engineDescriptors),
    busRange: busExtent ? busExtent.max - busExtent.min : null,
    maxTit: turboPeakTit,
    titP95: turboP95Tit,
    maxMap: turboPeakMap,
    mapP95: turboP95Map,
    peakUpperDeck: turboPeakUpperDeck,
    findingTitle: summary.finding?.title ?? "No finding",
    findingMeta: summary.finding?.meta ?? "",
    findingClass: summary.overall.className,
    sensorScore: summary.sensors.score,
    sensorKey: sensorTop.key ?? null,
    sensorLabel: sensorTop.label ?? null,
    sensorSuspicion: sensorTop.suspicion ?? null,
    sensorZeroCount: sensorTop.zeroCount ?? 0,
    sensorOutOfRangeCount: sensorTop.outOfRangeCount ?? 0,
    sensorImpossibleJumpCount: sensorTop.impossibleJumpCount ?? 0,
    sensorPeerMismatchCount: (sensorTop.coldMismatchCount ?? 0) + (sensorTop.hotMismatchCount ?? 0),
    sensorIssues,
    oilSystemIssueLabel: summary.oilSystem.details?.top?.issue?.label ?? null,
    oilSystemIssueDetail: summary.oilSystem.details?.top?.issue?.detail ?? null,
    oilSystemIssueValue: summary.oilSystem.details?.top?.issue?.value ?? null,
    oilSystemIssueUnit: summary.oilSystem.details?.top?.issue?.unit ?? null,
    oilSystemChannelLabel: summary.oilSystem.details?.top?.label ?? null,
    oilSystemChannelKey: summary.oilSystem.details?.top?.key ?? null,
    oilSystemMin: summary.oilSystem.details?.top?.min ?? null,
    oilSystemMax: summary.oilSystem.details?.top?.max ?? null,
    oilSystemWindowCount: summary.oilSystem.details?.windows?.length ?? 0,
    oilSystemWindows: (summary.oilSystem.details?.windows ?? [])
      .filter((window) => Number.isFinite(window.startTime))
      .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))
      .slice(0, 6)
      .map((window) => ({
        startTime: window.startTime ?? null,
        endTime: window.endTime ?? null,
        duration: window.duration ?? null,
        label: window.label ?? null,
        key: window.key ?? null,
        channelLabel: window.channelLabel ?? null,
        kind: window.kind ?? null,
        worstValue: window.worstValue ?? null,
      })),
    valveCylinder: summary.valve.details.top?.cylinder ?? null,
    valveAmplitude: summary.valve.details.top?.best?.amplitude ?? null,
    valveRatio: summary.valve.details.top?.best?.ratio ?? null,
    valveBand: summary.valve.details.top?.best?.frequency?.band ?? null,
    valveFrequencyCpm: summary.valve.details.top?.best?.frequency?.frequencyCpm ?? null,
    valveCycleSeconds: summary.valve.details.top?.best?.frequency?.cycleSeconds ?? null,
    valveWindowSeconds: Number.isFinite(summary.valve.details.top?.best?.frequency?.durationSeconds)
      ? summary.valve.details.top.best.frequency.durationSeconds
      : (Number.isFinite(summary.valve.details.top?.best?.startTime) && Number.isFinite(summary.valve.details.top?.best?.endTime)
        ? Math.max(0, summary.valve.details.top.best.endTime - summary.valve.details.top.best.startTime)
        : null),
    valveCyclesInWindow: summary.valve.details.top?.best?.frequency?.cyclesInWindow ?? null,
    valveFlaggedWindows: summary.valve.details.top?.suspiciousWindows ?? 0,
    valveCheckedWindows: summary.valve.details.candidates?.filter((candidate) => candidate.cylinder === summary.valve.details.top?.cylinder).length
      ?? summary.valve.details.windows
      ?? 0,
    valveNote: summary.valve.note,
    combustionCylinder: combustionTop.cylinder ?? null,
    combustionSignature: combustionTop.type ?? null,
    combustionLabel: combustionTop.label ?? summary.combustion.note ?? null,
    combustionMeta: summary.combustion.details.meta ?? combustionEvidenceMeta(combustionTop) ?? null,
    combustionEgtChange: combustionTop.egtChange ?? null,
    combustionChtChange: combustionTop.chtChange ?? null,
    combustionCommonEgtChange: combustionTop.commonEgtChange ?? null,
    combustionCommonChtChange: combustionTop.commonChtChange ?? null,
    combustionStartTime: combustionTop.startTime ?? null,
    combustionEndTime: combustionTop.endTime ?? null,
    combustionPowerMovementDetail: combustionTop.powerMovement?.detail ?? null,
    combustionPowerMovementScore: combustionTop.powerMovementScore ?? combustionTop.powerMovement?.score ?? null,
    combustionFuelFlowThermalDetail: combustionTop.fuelFlowThermal?.detail ?? null,
    divergenceCylinders: divergenceTop.cylinders ?? [],
    divergenceEgtLift: divergenceTop.egtLift ?? null,
    divergenceChtLift: divergenceTop.chtLift ?? null,
    divergenceStartTime: divergenceTop.startTime ?? null,
    divergenceEndTime: divergenceTop.endTime ?? null,
    divergenceDuration: divergenceTop.duration ?? null,
    divergenceMode: divergenceTop.mode ?? null,
    divergenceEventCount: divergenceEvents.length,
    divergenceEvents,
    divergencePowerDetail: divergenceTop.power?.detail ?? null,
    divergencePowerStable: divergenceTop.power?.stable ?? null,
    descentEgtDropoutCylinder: summary.descentEgtDropout.details.top?.cylinder ?? null,
    descentEgtDropoutF: summary.descentEgtDropout.details.top?.dropoutF ?? null,
    descentEgtDropoutStartTime: summary.descentEgtDropout.details.top?.startTime ?? null,
    descentEgtDropoutEndTime: summary.descentEgtDropout.details.top?.endTime ?? null,
    descentEgtDropoutEvents,
    inductionLeakStartTime: inductionLeakTop.startTime ?? null,
    inductionLeakEndTime: inductionLeakTop.endTime ?? null,
    inductionLeakRpmChange: inductionLeakTop.rpmChange ?? null,
    inductionLeakFuelFlowChange: inductionLeakTop.fuelFlowChange ?? null,
    inductionLeakMapChange: inductionLeakTop.mapChange ?? null,
    inductionLeakEgtChange: inductionLeakTop.egtChange ?? null,
    inductionLeakCylinder: cylinderFromMetricTop(inductionLeakTop),
    inductionLeakChannelKey: inductionLeakTop.key ?? null,
    inductionLeakCylinderEgtChange: inductionLeakTop.cylinderEgtChange ?? null,
    inductionLeakMode: inductionLeakTop.mode ?? null,
    inductionLeakPeerDelta: inductionLeakTop.cylinderPeerDelta ?? null,
    magCylinder: summary.mag.details.top?.weakResponses?.[0]?.cylinder ?? null,
    magRpmDrop: summary.mag.details.top?.rpmDrop ?? null,
    carbIceMode: carbIceTop.mode ?? null,
    carbIceCarbTemp: carbIceTop.carbTempMedian ?? null,
    carbIceRpmChange: carbIceTop.rpmChange ?? null,
    carbIceMapChange: carbIceTop.mapChange ?? null,
    coolingCylinder: coolingTop.cylinder ?? null,
    coolingP95Delta: coolingTop.p95Delta ?? null,
    coolingP95Cht: coolingTop.p95Cht ?? null,
    coolingStartTime: coolingTop.evidenceWindow?.startTime ?? null,
    coolingEndTime: coolingTop.evidenceWindow?.endTime ?? null,
    coolingRateCylinder: coolingRateTop.cylinder ?? null,
    coolingRateFpm: coolingRateTop.rateFpm ?? null,
    coolingRateStartTime: coolingRateTop.startTime ?? null,
    coolingRateEndTime: coolingRateTop.endTime ?? null,
    coolingRatePercentPowerDelta: coolingRateTop.percentPowerDelta ?? null,
    coolingRateEvents,
    chargingStartTime: summary.charging.details?.startTime ?? null,
    chargingEndTime: summary.charging.details?.endTime ?? null,
    chargingStatus: summary.charging.details?.status ?? null,
    hottestCylinder: summary.cht.details.hottest?.cylinder ?? null,
    chtByCylinder: chtByCylinderSummary(points, { engineDescriptors }),
    instrumentationSuspicionScore: Number.isFinite(instrumentationSuspicion?.score) ? instrumentationSuspicion.score : null,
    instrumentationSuspicionNote: instrumentationSuspicion?.note ?? null,
    instrumentationSuspicionFlaggedChannels: instrumentationSuspicion?.details?.flaggedChannels ?? [],
    firstTime: formatPointTime(first),
    lastTime: formatPointTime(last),
  };
}
